import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { useForm } from 'react-hook-form';
import { type TUpdateAdminData } from 'views/types/admins';
import { Button } from 'components/button';
import { adminService } from 'views/services/adminService';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { toast } from 'App';
import { IoMdClose } from 'react-icons/io';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { useAdminPermissions } from 'hooks/useAdminPermissions';
import { AdminLogs } from '../AdminLogs';

export const EditAdminForm = () => {
  const { selectedAdmin } = useAdminsStore();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [roles, setRoles] = useState<
    { id: string; name: string; description: string }[]
  >([]);

  const { setSelectedAdmin, admins, currentAdmin } = useAdminsStore();
  const { canUpdate, canDelete } = usePermissions();
  const { canEditAdmin, getAvailableRoles, canAssignRole } =
    useAdminPermissions();

  const hasEditPermission = canEditAdmin(selectedAdmin, currentAdmin);
  const availableRoles = getAvailableRoles(currentAdmin?.role || '', roles);

  const generatePasswordPlaceholder = () => {
    const minLength = 8;
    const maxLength = 16;
    const length =
      Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
    return '•'.repeat(length);
  };

  const {
    register,
    handleSubmit,
    formState: { dirtyFields, errors },
    reset,
    resetField,
  } = useForm<TUpdateAdminData>({
    defaultValues: {
      name: '',
      telegramId: '',
      isActive: false,
      role: '',
      password: '',
    },
    mode: 'onChange',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (selectedAdmin) {
      reset({
        name: selectedAdmin.name ?? '',
        telegramId: selectedAdmin.telegramId ?? '',
        isActive: selectedAdmin.isActive ?? false,
        role: selectedAdmin.role,
        password: '',
      });
      resetField('telegramId', {
        defaultValue: selectedAdmin.telegramId ?? '',
      });
    }
  }, [selectedAdmin, reset, resetField]);

  useEffect(() => {
    const loadRoles = async () => {
      const rolesData = await adminService.getRoles();
      setRoles(rolesData);
    };
    loadRoles();
  }, []);

  const onSubmit = async (values: TUpdateAdminData) => {
    if (!selectedAdmin) return;

    if (!hasEditPermission) {
      toast({
        title: 'Access Denied',
        description:
          'You do not have permission to change this user information',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (
      'role' in dirtyFields &&
      (!canUpdate(PERMISSION_RESOURCES.ADMINS) ||
        !canUpdate(PERMISSION_RESOURCES.ASSIGN_ROLE))
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to change admin roles',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if ('role' in dirtyFields && values.role) {
      if (!canAssignRole(values.role, currentAdmin?.role || '', roles)) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to assign this role',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    if (
      'isActive' in dirtyFields &&
      (!canUpdate(PERMISSION_RESOURCES.ADMINS) ||
        !canUpdate(PERMISSION_RESOURCES.ACTIVE_INACTIVE_CTA))
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to change admin active status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (
      'password' in dirtyFields &&
      (!canUpdate(PERMISSION_RESOURCES.ADMINS) ||
        !canUpdate(PERMISSION_RESOURCES.PASSWORD))
    ) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to change admin passwords',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const changedFields: Partial<TUpdateAdminData> = {};
    for (const key in dirtyFields) {
      const field = key as keyof TUpdateAdminData;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      changedFields[field] = values[field];
    }

    if (Object.keys(changedFields).length > 0) {
      // Check if user has update permission for admins
      if (!canUpdate(PERMISSION_RESOURCES.ADMINS)) {
        toast({
          title: 'Error',
          description:
            'You do not have permission to update admin information.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
      if (currentAdmin.id === selectedAdmin.id && 'isActive' in changedFields) {
        toast({
          title: 'Error',
          description: "You can't block yourself.",
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });

        return;
      }
      const updateData = await adminService.updateAdmin(
        changedFields,
        selectedAdmin.id
      );

      if (updateData) {
        setSelectedAdmin(null);
        window.location.reload();
      }
    } else {
      setSelectedAdmin(null);
    }
  };

  const onDelete = async () => {
    if (!selectedAdmin) return;

    if (!hasEditPermission) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to delete this user',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!canDelete(PERMISSION_RESOURCES.ADMINS)) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to delete admins',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!canBeDeleted) {
      toast({
        title: 'Error',
        description: 'Cannot remove the last super-admin',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    await adminService.deleteAdmin(selectedAdmin.id);
    setConfirmOpen(false);
    setSelectedAdmin(null);
  };

  const canBeDeleted =
    selectedAdmin?.role !== 'admin' ||
    admins.filter(admin => admin.role === 'admin').length > 1;

  if (!selectedAdmin) return null;

  return (
    <>
      <Dialog.Root
        open={!!selectedAdmin}
        onOpenChange={() => setSelectedAdmin(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
          <Dialog.Content
            onInteractOutside={e => {
              if ((e.target as HTMLElement).closest('.chakra-toast')) {
                e.preventDefault();
              }
            }}
            className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] h-[85vh] w-[90vw] max-w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-lg focus:outline-none dark:bg-gray-800 dark:text-white"
            aria-describedby="edit-admin-description"
          >
            <button
              onClick={() => setSelectedAdmin(null)}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
              aria-label="Close"
            >
              <IoMdClose />
            </button>

            <Dialog.Title className="text-center text-lg font-medium mb-6">
              Edit Admin
            </Dialog.Title>
            <Dialog.Description id="edit-admin-description" className="sr-only">
              Edit administrator information including name, telegram ID,
              password, active status, and role
            </Dialog.Description>

            {!hasEditPermission ? (
              <div className="flex items-center justify-center min-h-[120px] h-full w-full overflow-hidden">
                <p className="text-red-500 text-lg font-medium text-center mx-auto">
                  You do not have permission to change this user's information.
                </p>
              </div>
            ) : (
              <Tabs.Root
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full overflow-y-auto max-h-[calc(85vh-120px)]"
              >
                <Tabs.List className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                  <Tabs.Trigger
                    value="info"
                    className="px-4 py-2 text-sm font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:text-gray-300 dark:data-[state=active]:text-blue-400"
                  >
                    Info
                  </Tabs.Trigger>
                  <ReadPermissionGate
                    resource={PERMISSION_RESOURCES.ADMIN_LOGS}
                  >
                    <Tabs.Trigger
                      value="logs"
                      className="px-4 py-2 text-sm font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:text-gray-300 dark:data-[state=active]:text-blue-400"
                    >
                      Logs
                    </Tabs.Trigger>
                  </ReadPermissionGate>
                </Tabs.List>

                <Tabs.Content value="info" className="space-y-4">
                  <form
                    key={selectedAdmin?.id}
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block mb-1">Name</label>
                      <input
                        disabled={!canUpdate(PERMISSION_RESOURCES.ADMINS)}
                        type="text"
                        {...register('name', {
                          required: 'Name is required',
                          maxLength: {
                            value: 20,
                            message: 'Maximum 20 characters',
                          },
                        })}
                        className={`dark:bg-[transparent] w-full rounded border px-3 py-2 ${errors?.name ? 'border-red-500' : ''}`}
                      />
                      {errors?.name && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block mb-1">Telegram ID</label>
                      <input
                        type="text"
                        disabled={!canUpdate(PERMISSION_RESOURCES.ADMINS)}
                        {...register('telegramId', {
                          required: 'Telegram ID is required',
                          maxLength: {
                            value: 20,
                            message: 'Maximum 20 characters',
                          },
                          pattern: {
                            value: /^\d*$/,
                            message: 'Numbers only',
                          },
                        })}
                        className={`dark:bg-[transparent] w-full rounded border px-3 py-2 ${errors?.telegramId ? 'border-red-500' : ''}`}
                      />
                      {errors?.telegramId && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.telegramId.message}
                        </p>
                      )}

                      <ReadPermissionGate
                        resource={PERMISSION_RESOURCES.PASSWORD}
                      >
                        <label className="block mb-1">Password</label>
                        <input
                          disabled={
                            !canUpdate(PERMISSION_RESOURCES.PASSWORD) ||
                            !canUpdate(PERMISSION_RESOURCES.ADMINS)
                          }
                          type="password"
                          placeholder={generatePasswordPlaceholder()}
                          autoComplete="new-password"
                          {...register('password', {
                            minLength: {
                              value: 8,
                              message:
                                'Password must be at least 8 characters long',
                            },
                            pattern: {
                              value:
                                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#@$!%*?&])[A-Za-z\d#@$!%*?&]{8,}$/,
                              message:
                                'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character',
                            },
                          })}
                          className={`dark:bg-[transparent] w-full rounded border px-3 py-2 ${errors?.password ? 'border-red-500' : ''}`}
                        />
                        {errors?.password && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.password.message}
                          </p>
                        )}
                      </ReadPermissionGate>
                    </div>

                    <ReadPermissionGate
                      resource={PERMISSION_RESOURCES.ACTIVE_INACTIVE_CTA}
                    >
                      <div>
                        <label className="block mb-1">Active</label>
                        <input
                          type="checkbox"
                          {...register('isActive')}
                          className="dark:bg-[transparent] mr-2"
                          disabled={
                            !canUpdate(PERMISSION_RESOURCES.ADMINS) ||
                            !canUpdate(PERMISSION_RESOURCES.ACTIVE_INACTIVE_CTA)
                          }
                        />
                      </div>
                    </ReadPermissionGate>

                    <ReadPermissionGate
                      resource={PERMISSION_RESOURCES.ASSIGN_ROLE}
                    >
                      <div>
                        <label className="block mb-1">Role</label>
                        <select
                          {...register('role')}
                          className="dark:bg-gray-800 w-full rounded border px-3 py-2"
                          disabled={
                            !canUpdate(PERMISSION_RESOURCES.ADMINS) ||
                            !canUpdate(PERMISSION_RESOURCES.ASSIGN_ROLE) ||
                            availableRoles.length === 0
                          }
                        >
                          {availableRoles.length === 0 ? (
                            <option value="" disabled>
                              No roles available
                            </option>
                          ) : (
                            availableRoles.map(role => (
                              <option
                                className="dark:bg-gray-800"
                                key={role.id}
                                value={role.name}
                              >
                                {role.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </ReadPermissionGate>

                    <div className="text-center flex justify-start gap-3 pt-4">
                      <Button
                        type="submit"
                        disabled={!canUpdate(PERMISSION_RESOURCES.ADMINS)}
                      >
                        Save Changes
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!canDelete(PERMISSION_RESOURCES.ADMINS)}
                      >
                        Delete Admin
                      </Button>
                    </div>
                  </form>
                </Tabs.Content>

                <Tabs.Content value="logs" className="space-y-4">
                  <ReadPermissionGate
                    resource={PERMISSION_RESOURCES.ADMIN_LOGS}
                  >
                    {selectedAdmin && <AdminLogs adminId={selectedAdmin.id} />}
                  </ReadPermissionGate>
                </Tabs.Content>
              </Tabs.Root>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[999] bg-black/30 backdrop-blur-sm" />
          <Dialog.Content className="fixed top-1/2 left-1/2 z-[1000] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-bold mb-4">
              Delete Admin
            </Dialog.Title>
            <p className="mb-4">
              Are you sure you want to delete admin "
              <strong>{selectedAdmin?.name}</strong>"?
              <br />
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-300 rounded">
                  Cancel
                </button>
              </Dialog.Close>
              <button
                onClick={onDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Confirm
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
